/**
 * Export and import utilities for LinCal calendar data
 */

import { CURRENT_VERSION } from './constants';

/**
 * Export data as JSON file download
 * @param {Object} data - Calendar data to export
 */
export function exportToJSON(data) {
  try {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `lincal-backup-${date}.json`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting data:', error);
    throw new Error('Failed to export data');
  }
}

/**
 * Escape text for iCalendar format
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeICalText(text) {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')  // Backslash first
    .replace(/;/g, '\\;')     // Semicolon
    .replace(/,/g, '\\,')     // Comma
    .replace(/\n/g, '\\n');   // Newline
}

/**
 * Fold long lines at 75 characters per iCalendar spec
 * @param {string} line - Line to fold
 * @returns {string} Folded line with CRLF + space
 */
function foldLine(line) {
  if (line.length <= 75) return line;
  
  const lines = [];
  let remaining = line;
  
  // First line can be 75 chars
  lines.push(remaining.substring(0, 75));
  remaining = remaining.substring(75);
  
  // Continuation lines are 74 chars (accounting for leading space)
  while (remaining.length > 0) {
    lines.push(' ' + remaining.substring(0, 74));
    remaining = remaining.substring(74);
  }
  
  return lines.join('\r\n');
}

/**
 * Export events and milestones to iCalendar (.ics) format
 * Uses VALUE=DATE for all-day events (timezone-agnostic)
 * 
 * @param {Array} events - Events to export
 * @param {Array} milestones - Milestones to export  
 * @param {Array} projects - Projects for category mapping
 */
export function exportToICS(events, milestones, projects) {
  try {
    // Create project ID to name mapping
    const projectMap = {};
    projects.forEach(p => {
      projectMap[p.id] = p.name;
    });
    
    // Build iCalendar content
    const lines = [];
    
    // Calendar header
    lines.push('BEGIN:VCALENDAR');
    lines.push('VERSION:2.0');
    lines.push('PRODID:-//LinCal//LinCal Export//EN');
    lines.push('CALSCALE:GREGORIAN');
    lines.push('METHOD:PUBLISH');
    lines.push('X-WR-CALNAME:LinCal Export');
    
    // Current timestamp for DTSTAMP (required field)
    const now = new Date();
    const dtstamp = now.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    
    // Export events
    events.forEach(event => {
      // Skip events with missing dates
      if (!event.startDate || !event.endDate) {
        console.warn('Skipping event with missing dates:', event);
        return;
      }
      
      const projectName = projectMap[event.projectId] || 'Unknown';
      
      // Parse dates and format as YYYYMMDD
      const startDate = event.startDate.replace(/-/g, '');
      
      // DTEND is exclusive for all-day events, so add 1 day
      const endDateObj = new Date(event.endDate + 'T00:00:00');
      if (isNaN(endDateObj.getTime())) {
        console.warn('Skipping event with invalid end date:', event);
        return;
      }
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDate = endDateObj.toISOString().split('T')[0].replace(/-/g, '');
      
      lines.push('BEGIN:VEVENT');
      lines.push(foldLine(`UID:${event.id}@lincal`));
      lines.push(foldLine(`DTSTAMP:${dtstamp}`));
      lines.push(foldLine(`DTSTART;VALUE=DATE:${startDate}`));
      lines.push(foldLine(`DTEND;VALUE=DATE:${endDate}`));
      lines.push(foldLine(`SUMMARY:${escapeICalText(event.title)}`));
      
      if (event.description) {
        lines.push(foldLine(`DESCRIPTION:${escapeICalText(event.description)}`));
      }
      
      lines.push(foldLine(`CATEGORIES:${escapeICalText(projectName)}`));
      lines.push('END:VEVENT');
    });
    
    // Export milestones as single-day events
    const iconMap = {
      flag: '⚑',
      diamond: '◆',
      star: '★',
      circle: '●'
    };
    
    milestones.forEach(milestone => {
      // Skip milestones with missing date
      if (!milestone.date) {
        console.warn('Skipping milestone with missing date:', milestone);
        return;
      }
      
      const projectName = projectMap[milestone.projectId] || 'Unknown';
      const icon = iconMap[milestone.icon] || '●';
      
      // Format date as YYYYMMDD
      const startDate = milestone.date.replace(/-/g, '');
      
      // DTEND is next day for single-day all-day event
      const endDateObj = new Date(milestone.date + 'T00:00:00');
      if (isNaN(endDateObj.getTime())) {
        console.warn('Skipping milestone with invalid date:', milestone);
        return;
      }
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDate = endDateObj.toISOString().split('T')[0].replace(/-/g, '');
      
      lines.push('BEGIN:VEVENT');
      lines.push(foldLine(`UID:${milestone.id}@lincal`));
      lines.push(foldLine(`DTSTAMP:${dtstamp}`));
      lines.push(foldLine(`DTSTART;VALUE=DATE:${startDate}`));
      lines.push(foldLine(`DTEND;VALUE=DATE:${endDate}`));
      lines.push(foldLine(`SUMMARY:${escapeICalText(milestone.title)} [${icon}]`));
      
      if (milestone.description) {
        lines.push(foldLine(`DESCRIPTION:${escapeICalText(milestone.description)}`));
      }
      
      lines.push(foldLine(`CATEGORIES:${escapeICalText(projectName)}`));
      lines.push('END:VEVENT');
    });
    
    // Calendar footer
    lines.push('END:VCALENDAR');
    
    // Join with CRLF as per iCalendar spec
    const icsContent = lines.join('\r\n');
    
    // Create and download file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `lincal-${date}.ics`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting to iCalendar:', error);
    throw new Error('Failed to export iCalendar file');
  }
}

/**
 * Import data from JSON file
 * @param {File} file - JSON file to import
 * @returns {Promise<Object>} Parsed calendar data
 */
export function importFromJSON(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Validate structure
        if (!data.projects || !data.events || !data.milestones) {
          reject(new Error('Invalid file format: missing required fields'));
          return;
        }
        
        // Validate arrays
        if (!Array.isArray(data.projects) || !Array.isArray(data.events) || !Array.isArray(data.milestones)) {
          reject(new Error('Invalid file format: fields must be arrays'));
          return;
        }
        
        // Add version if missing
        if (!data.version) {
          data.version = CURRENT_VERSION;
        }
        
        resolve(data);
      } catch (error) {
        reject(new Error('Failed to parse JSON file: ' + error.message));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}
